import { ApolloClient } from "apollo-client";
import { ApolloLink } from "apollo-link";
import { createHttpLink } from "apollo-link-http";
import { InMemoryCache } from "apollo-cache-inmemory";
import { onError } from "apollo-link-error";
import { setContext } from "apollo-link-context";
import { withClientState } from "apollo-link-state";
import jwtDecode from "jwt-decode";
import * as R from "ramda";

import { getAuthObject, setAuthObject } from "../localStorage";
import queries from "./queries";
import { isAccessTokenExpired } from "./auth";

// eslint-disable-next-line no-undef
const BASE_URL = process.env.BASE_URL || "";
const GRAPHQL_API_URL = `${BASE_URL}/api/graphql`;

let client = null;

const hashcode = obj => {
    let hash = 0;
    const chars = JSON.stringify(obj);
    const len = chars.length;
    for (let i = 0; i < len; i++) {
        hash = (hash << 5) - hash + chars.charCodeAt(i);
        hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
};

// https://www.apollographql.com/docs/react/advanced/caching.html
// Alternative cache keys to prevent apollo normalizing on nonunique ids.
const cache = new InMemoryCache({
    dataIdFromObject: object => {
        switch (object.__typename) {
            case "arachnys_entities_Entity_external_identifiers":
                return object.id + object.lineOfBusiness;
            case "Entity":
                return R.pathOr(hashcode(object), ["key", "ulid"], object);
            default:
                return object.id || object._id || object.ulid; // default fall back
        }
    },
});

export const withBasicAuth = (username, password, token) => {
    return {
        mutation: queries.LOGIN,
        variables: { username, password, token },
    };
};

export const withRefreshToken = refreshToken => {
    return {
        mutation: queries.ROTATE_TOKENS,
        variables: { refreshToken },
    };
};

export const withSwitchUser = (refreshToken, toUser) => {
    return {
        mutation: queries.SWITCH_USER,
        variables: {
            refreshToken,
            toUser,
        },
        force: true,
    };
};

const updateLdUser = (ldClient, authData) => {
    const userConfig = {
        key: authData.username,
        firstName: authData.first_name,
        lastName: authData.last_name,
        email: authData.email,
        custom: {
            client: authData.client_name,
        },
    };
    ldClient.identify(userConfig);
};

export const authenticate = client => async ({ force, ...strategy }) => {
    const query = queries.GET_AUTH_TOKENS;
    const data = client.readQuery({ query });

    if (!force && !isAccessTokenExpired(data.auth.accessToken)) {
        return data;
    }

    const res = await client.mutate({
        ...strategy,
        fetchPolicy: "no-cache",
    });

    if (!res.data.auth.__typename.includes("Success")) {
        throw res.data.auth.message;
    }

    const { accessToken, refreshToken } = res.data.auth;
    const newData = {
        ...data,
        auth: {
            ...data.auth,
            accessToken: {
                __typename: "Token",
                ...data.auth.accessToken,
                requires_2fa_setup: false,
                switched_from: null,
                raw: accessToken,
                ...jwtDecode(accessToken),
            },
            refreshToken: {
                __typename: "Token",
                ...data.auth.refreshToken,
                requires_2fa_setup: false,
                switched_from: null,
                raw: refreshToken,
                ...jwtDecode(refreshToken),
            },
        },
    };

    client.writeData({ data: newData });
    setAuthObject(newData.auth);

    if (client.ldClient !== undefined) {
        updateLdUser(client.ldClient, newData.auth.accessToken);
    }

    return newData;
};

export const getCurrentRefreshToken = () => {
    const query = queries.GET_AUTH_TOKENS;
    const currentData = client.readQuery({ query });
    return R.path(["auth", "refreshToken"], currentData);
};

export const obtainAccessToken = async () => {
    const refreshToken = getCurrentRefreshToken();
    if (!refreshToken) {
        return null;
    }

    return authenticate(client)(withRefreshToken(refreshToken.raw));
};

export const switchUser = async toUserID => {
    const refreshToken = getCurrentRefreshToken();
    if (!refreshToken) {
        return null;
    }

    // Switch to the original user if we are currently posing
    // as another user.
    if (refreshToken.switched_from) {
        toUserID = refreshToken.switched_from;
    }

    return authenticate(client)(withSwitchUser(refreshToken.raw, toUserID));
};

const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
        graphQLErrors.map(({ message, locations, path }) =>
            // eslint-disable-next-line no-console
            console.error(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            )
        );
    // eslint-disable-next-line no-console
    if (networkError) console.error(`[Network error]: ${networkError}`);
});

const authLink = setContext(async (ctx, { headers }) => {
    if (R.startsWith("Public_", ctx.operationName)) {
        return null;
    }

    const res = await obtainAccessToken();
    const accessToken = R.path(["auth", "accessToken"], res);
    if (!accessToken) {
        return null;
    }

    return {
        headers: {
            ...headers,
            authorization:
                accessToken && accessToken.raw
                    ? `Bearer ${accessToken.raw}`
                    : "",
        },
    };
});

const defaultAuth = {
    __typename: "Auth",
    accessToken: null,
    refreshToken: null,
};

const stateLink = withClientState({
    cache,
    resolvers: {
        Query: {
            auth: () => {
                return defaultAuth;
            },
        },
    },
    defaults: {
        auth: defaultAuth,
    },
});

const httpLink = createHttpLink({
    uri: GRAPHQL_API_URL,
    credentials: "same-origin",
});

const defaultOptions = {
    // WARNING: this does not quite work, see: https://github.com/apollographql/apollo-client/issues/2555
    query: {
        fetchPolicy: "cache-and-network",
    },
};

client = new ApolloClient({
    link: ApolloLink.from([errorLink, stateLink, authLink, httpLink]),
    cache,
    defaultOptions,
});

client.onResetStore(stateLink.writeDefaults);

const storedAuthObject = getAuthObject();

if (storedAuthObject) {
    client.writeData({
        data: { auth: storedAuthObject },
    });
}

export default client;
